import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Download, FileText, ExternalLink } from "lucide-react";
import PDFViewer from "../components/PDFViewer";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function NotesViewerPage() {
  const [searchParams] = useSearchParams();
  const fileUrl = searchParams.get("file") || "";
  const title = searchParams.get("title") || "Notes";
  const initialPage = parseInt(searchParams.get("page") || "1", 10);
  const [downloading, setDownloading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [pdfError, setPdfError] = useState(false);

  // Construct full URL
  const fullFileUrl = fileUrl.startsWith("http") ? fileUrl : `${BACKEND}${fileUrl}`;
  
  // Check file type
  const isImage = fileUrl.match(/\.(png|jpg|jpeg|gif|webp)$/i);
  const isPdf = fileUrl.match(/\.pdf$/i) || (fullFileUrl.includes('cloudinary.com') && fullFileUrl.includes('/raw/upload/'));
  const isCloudinaryPdf = fullFileUrl.includes('cloudinary.com') && fullFileUrl.includes('/raw/upload/');
  
  // Get the URL for PDF viewing - use proxy for Cloudinary PDFs
  const getPdfUrl = () => {
    if (isCloudinaryPdf) {
      return `${BACKEND}/api/pdf-viewer?url=${encodeURIComponent(fullFileUrl)}`;
    }
    return fullFileUrl;
  };

  useEffect(() => {
    document.title = title ? `${title} - PassPoints` : "PassPoints";
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => {
      document.title = "PassPoints";
      window.removeEventListener('resize', checkMobile);
    };
  }, [title]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // For Cloudinary PDFs, use the proxy endpoint for download
      const downloadUrl = isCloudinaryPdf 
        ? `${BACKEND}/api/pdf-viewer?url=${encodeURIComponent(fullFileUrl)}`
        : fullFileUrl;
      
      const response = await fetch(downloadUrl, { mode: 'cors' });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const ext = isPdf ? '.pdf' : (isImage ? fileUrl.match(/\.(png|jpg|jpeg|gif|webp)$/i)?.[0] || '' : '');
      link.download = `${title || 'document'}${ext}`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      console.error('Download error:', error);
      // Fallback - open original URL in new tab
      window.open(fullFileUrl, '_blank');
    }
    setDownloading(false);
  };

  const handleClose = () => {
    window.close();
  };

  if (!fileUrl) {
    return (
      <div className="min-h-screen pt-24 pb-12 flex items-center justify-center bg-zinc-950">
        <div className="text-center">
          <FileText className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-white mb-2">No File Selected</h1>
          <p className="text-zinc-500 mb-6">Please select a note to view</p>
          <button onClick={handleClose} className="text-violet-400 hover:text-violet-300">
            Close Tab
          </button>
        </div>
      </div>
    );
  }

  // Image Viewer
  if (isImage) {
    return (
      <div className="min-h-screen bg-zinc-950">
        {/* Header */}
        <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 py-3 bg-zinc-900/95 backdrop-blur-md border-b border-zinc-800">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <button 
              onClick={handleClose}
              className="flex items-center gap-1 text-sm text-zinc-400 hover:text-white transition-colors shrink-0"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Close</span>
            </button>
            <div className="h-5 w-px bg-zinc-700 shrink-0" />
            <h1 className="text-sm sm:text-base font-medium text-white truncate">
              {title}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={fullFileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
              title="Open Original"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-sm hover:bg-violet-500 transition-colors shrink-0 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">{downloading ? "..." : "Download"}</span>
            </button>
          </div>
        </div>

        {/* Image Content */}
        <div className="pt-16 min-h-screen flex items-center justify-center p-4">
          {imageError ? (
            <div className="text-center">
              <FileText className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-white mb-2">Image Not Found</h2>
              <p className="text-sm text-zinc-500 mb-4">This file may have been moved or deleted</p>
              <button
                onClick={handleDownload}
                className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm hover:bg-violet-500"
              >
                Try Download
              </button>
            </div>
          ) : (
            <motion.img 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              src={fullFileUrl} 
              alt={title}
              className="max-w-full max-h-[calc(100vh-100px)] object-contain rounded-lg shadow-2xl"
              onError={() => setImageError(true)}
            />
          )}
        </div>
      </div>
    );
  }

  // PDF Viewer with fallback
  if (isPdf) {
    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-zinc-900/95 backdrop-blur-md border-b border-zinc-800 shrink-0">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <button 
              onClick={handleClose}
              className="flex items-center gap-1 text-sm text-zinc-400 hover:text-white transition-colors shrink-0"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Close</span>
            </button>
            <div className="h-5 w-px bg-zinc-700 shrink-0" />
            <h1 className="text-sm sm:text-base font-medium text-white truncate">
              {title}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={fullFileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
              title="Open Original"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-sm hover:bg-violet-500 transition-colors shrink-0 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">{downloading ? "..." : "Download"}</span>
            </button>
          </div>
        </div>

        {/* PDF Viewer */}
        <div className="flex-1 min-h-0">
          {pdfError ? (
            <div className="w-full h-full flex items-center justify-center bg-zinc-900">
              <div className="text-center p-6">
                <FileText className="w-16 h-16 text-violet-500 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-white mb-2">{title}</h2>
                <p className="text-sm text-zinc-500 mb-4">Unable to load PDF viewer. Choose an option:</p>
                <div className="flex gap-3 justify-center flex-wrap">
                  <a
                    href={fullFileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-500 transition-colors"
                  >
                    Open in New Tab
                  </a>
                  <button
                    onClick={handleDownload}
                    className="px-6 py-3 rounded-lg bg-zinc-700 text-white text-sm font-medium hover:bg-zinc-600 transition-colors"
                  >
                    Download PDF
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <PDFViewer 
              url={getPdfUrl()} 
              title={title}
              initialPage={initialPage}
              onError={() => setPdfError(true)}
              onDownload={handleDownload}
            />
          )}
        </div>
      </div>
    );
  }

  // Fallback for unknown file types
  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <div className="text-center p-6">
        <FileText className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-white mb-2">{title}</h2>
        <p className="text-sm text-zinc-500 mb-4">This file type cannot be previewed</p>
        <div className="flex gap-3 justify-center">
          <a
            href={fullFileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm hover:bg-violet-500"
          >
            Open File
          </a>
          <button
            onClick={handleDownload}
            className="px-4 py-2 rounded-lg bg-zinc-700 text-white text-sm hover:bg-zinc-600"
          >
            Download
          </button>
        </div>
      </div>
    </div>
  );
}
